import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, message, Modal, Input, Spin, Empty, Table, Button as AntButton } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text, Button, Flex } from 'atoms';
import {
	acceptVendorBillChangesV1,
	rejectVendorBillChangesV1,
	searchInvoiceEditApprovalsV1,
} from 'store/approvalSlice';
import { ACL } from 'components/common';
import { SVG } from 'utils';

const { TextArea } = Input;

const Container = styled(Box)`
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const Title = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
`;

const Subtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
`;

const Card = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
`;

const CardRow = styled(Flex)`
	margin-bottom: 16px;
	gap: 40px;
`;

const FieldContainer = styled(Box)`
	min-width: 200px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	margin-bottom: 8px;
	display: block;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	display: block;
`;

const ActionButtons = styled(Flex)`
	gap: 12px;
	margin-top: 16px;
	justify-content: flex-end;
	align-items: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	cursor: pointer;
	color: #000;
	transition: all 0.2s ease;
	margin-right: 8px;
	padding: 0;

	svg {
		width: 20px !important;
		height: 20px !important;
	}

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	return `₹ ${Number(value).toLocaleString('en-IN', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

const formatTime = (dateString) => {
	if (!dateString) return '';
	return moment(dateString).format('HH:mm');
};

const formatDateTime = (dateString) => {
	if (!dateString) return '-';
	return (
		<>
			<div>{formatDate(dateString)}</div>
			<div style={{ fontSize: '12px', color: '#666' }}>{formatTime(dateString)}</div>
		</>
	);
};

export default function InvoiceEditApprovalOldUI() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [activeTab, setActiveTab] = useState('pending');
	const [approvals, setApprovals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApproval, setSelectedApproval] = useState(null);
	const [rejectionReason, setRejectionReason] = useState('');

	const fetchApprovals = useCallback(
		async (status) => {
			setLoading(true);
			try {
				// Map UI status to API status: approved -> accepted
				const apiStatus = status === 'approved' ? 'accepted' : status;

				// Use v1-specific search function for old UI
				const result = await dispatch(
					searchInvoiceEditApprovalsV1({
						status: apiStatus,
						limit: 20,
						offset: 0,
					})
				);

				if (result && result.approvals) {
					setApprovals(result.approvals);
				} else {
					setApprovals([]);
				}
			} catch (error) {
				console.error('Error fetching approvals:', error);
				message.error('Failed to fetch approvals');
				setApprovals([]);
			} finally {
				setLoading(false);
			}
		},
		[dispatch]
	);

	useEffect(() => {
		fetchApprovals(activeTab);
	}, [activeTab, fetchApprovals]);

	// Refresh when returning from details page
	useEffect(() => {
		if (location.state?.refresh) {
			fetchApprovals(activeTab);
			// Clear the refresh flag
			navigate(location.pathname, { replace: true, state: {} });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const handleApproveClick = (approval) => {
		setSelectedApproval(approval);
		setApproveModalVisible(true);
	};

	const handleRejectClick = (approval) => {
		setSelectedApproval(approval);
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!selectedApproval) return;

		const auditId = selectedApproval.audit_id || selectedApproval.id;
		if (!auditId) {
			message.error('Audit ID is missing');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(acceptVendorBillChangesV1({ audit_id: auditId }));

			if (result && result.success) {
				message.success('Invoice changes accepted successfully');
				setApproveModalVisible(false);
				setSelectedApproval(null);
				fetchApprovals(activeTab);
			} else {
				message.error(result?.error || 'Failed to accept invoice changes');
			}
		} catch (error) {
			console.error('Error accepting invoice changes:', error);
			message.error('Failed to accept invoice changes');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!selectedApproval) return;

		const auditId = selectedApproval.audit_id || selectedApproval.id;
		if (!auditId) {
			message.error('Audit ID is missing');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				rejectVendorBillChangesV1({
					audit_id: auditId,
					reason: rejectionReason || undefined,
				})
			);

			if (result && result.success) {
				message.success('Invoice changes rejected successfully');
				setRejectModalVisible(false);
				setSelectedApproval(null);
				setRejectionReason('');
				fetchApprovals(activeTab);
			} else {
				message.error(result?.error || 'Failed to reject invoice changes');
			}
		} catch (error) {
			console.error('Error rejecting invoice changes:', error);
			message.error('Failed to reject invoice changes');
		} finally {
			setActionLoading(false);
		}
	};

	const handleView = (entityId, auditId) => {
		// Navigate to approval details page with entity_id in URL and audit_id in state
		if (auditId && entityId) {
			navigate(`/approvals/invoice-edit-approval/${entityId}`, {
				state: { fromApproval: true, auditId, entityId },
			});
		} else if (entityId) {
			// Fallback to vendor invoice route if no audit ID
			navigate(`/vendor-invoice/${entityId}`, {
				state: { fromApproval: true, auditId },
			});
		}
	};

	// Helper to get field value from approval object
	const getFieldValue = (approval, field, fallback = '-') =>
		approval[field] || approval.entity_summary?.[field] || fallback;

	// Helper to get approved/rejected fields based on status
	const getStatusFields = (approval, isApproved, isRejected) => {
		if (isApproved) {
			return {
				approved_at: approval.updated_at || null,
				approved_by: approval.updated_by_username || '-',
				rejected_at: null,
				rejected_by: '-',
			};
		}
		if (isRejected) {
			return {
				approved_at: null,
				approved_by: '-',
				rejected_at: approval.updated_at || null,
				rejected_by: approval.updated_by_username || '-',
			};
		}
		return {
			approved_at: null,
			approved_by: '-',
			rejected_at: null,
			rejected_by: '-',
		};
	};

	const mapApprovalToTableData = (approval) => {
		// Map directly from API response structure
		const isApproved = approval.status === 'approved' || approval.status === 'accepted';
		const isRejected = approval.status === 'rejected';
		const entityId = approval.vendor_bill_id || approval.entity_id || approval.id;
		const auditId = approval.audit_id || approval.id;
		const statusFields = getStatusFields(approval, isApproved, isRejected);

		return {
			key: approval.id,
			id: entityId,
			vendor_bill_id: entityId,
			audit_id: auditId,
			invoice_no: getFieldValue(approval, 'invoice_no') || approval.vendor_bill_no || '-',
			project_name: getFieldValue(approval, 'project_name') || '-',
			vendor_name: getFieldValue(approval, 'vendor_name') || '-',
			initial_invoice_value: getFieldValue(approval, 'initial_invoice_value', 0),
			updated_invoice_value: getFieldValue(approval, 'updated_invoice_value', 0),
			billing_period: getFieldValue(approval, 'billing_period') || '-',
			created_at: approval.created_at || approval.requested_at || null,
			...statusFields,
		};
	};

	const getApprovedColumns = () => [
		{
			title: 'Approved at',
			key: 'approved_at',
			width: 180,
			render: (_, record) => formatDateTime(record.approved_at),
		},
		{
			title: 'Approved By',
			dataIndex: 'approved_by',
			key: 'approved_by',
			width: 150,
		},
	];

	const getRejectedColumns = () => [
		{
			title: 'Rejected at',
			key: 'rejected_at',
			width: 180,
			render: (_, record) => formatDateTime(record.rejected_at || record.approved_at),
		},
		{
			title: 'Rejected By',
			dataIndex: 'rejected_by',
			key: 'rejected_by',
			width: 150,
			render: (_, record) => record.rejected_by || '-',
		},
	];

	const getActionColumn = () => ({
		title: 'Action',
		key: 'action',
		width: 100,
		fixed: 'right',
		render: (_, record) => (
			<AntButton
				type="text"
				icon={<EyeOutlined />}
				onClick={() => handleView(record.vendor_bill_id || record.id, record.audit_id)}
				title="View Details"
			/>
		),
	});

	const getTableColumns = (tabKey) => {
		const baseColumns = [
			{
				title: 'Invoice No',
				dataIndex: 'invoice_no',
				key: 'invoice_no',
				width: 150,
			},
			{
				title: 'Project Name',
				dataIndex: 'project_name',
				key: 'project_name',
				width: 180,
			},
			{
				title: 'Vendor Name',
				dataIndex: 'vendor_name',
				key: 'vendor_name',
				width: 180,
			},
			{
				title: 'Initial Invoice Value',
				dataIndex: 'initial_invoice_value',
				key: 'initial_invoice_value',
				width: 180,
				render: (value) => formatCurrency(value),
			},
			{
				title: 'Updated Invoice Value',
				dataIndex: 'updated_invoice_value',
				key: 'updated_invoice_value',
				width: 180,
				render: (value) => formatCurrency(value),
			},
			{
				title: 'Billing Period',
				dataIndex: 'billing_period',
				key: 'billing_period',
				width: 150,
			},
			{
				title: 'Created at',
				key: 'created_at',
				width: 180,
				render: (_, record) => formatDateTime(record.created_at),
			},
		];

		// Add tab-specific columns
		if (tabKey === 'approved') {
			baseColumns.push(...getApprovedColumns());
		} else if (tabKey === 'rejected') {
			baseColumns.push(...getRejectedColumns());
		}

		// Add action column (eye icon) to all tabs
		baseColumns.push(getActionColumn());

		return baseColumns;
	};

	const renderTableContent = (tabKey) => {
		const tableData = approvals.map(mapApprovalToTableData);
		const columns = getTableColumns(tabKey);

		return (
			<Box>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} items`,
						position: ['bottomRight'],
					}}
					scroll={{ x: 'max-content' }}
					locale={{
						emptyText: (
							<Empty
								description={
									<Text fontSize="14px" color="#999">
										No {tabKey} invoice edit approvals found
									</Text>
								}
							/>
						),
					}}
				/>
			</Box>
		);
	};

	const renderApprovalCard = (approval) => {
		const entitySummary = approval.entity_summary || {};
		const invoice = {
			invoice_no: entitySummary.vendor_bill_no || approval.vendor_bill_no || '-',
			project_name: entitySummary.project_name || approval.project_name || '-',
			vendor_name: entitySummary.vendor_name || approval.vendor_name || '-',
			initial_invoice_value:
				entitySummary.initial_invoice_value || approval.initial_invoice_value || 0,
			updated_invoice_value:
				entitySummary.updated_invoice_value || approval.updated_invoice_value || 0,
			billing_period: entitySummary.billing_period || approval.billing_period || '-',
			created_at: approval.created_at || approval.requested_at || null,
		};

		return (
			<Card key={approval.id}>
				<CardRow>
					<FieldContainer>
						<FieldLabel>Invoice No</FieldLabel>
						<FieldValue>{invoice.invoice_no}</FieldValue>
					</FieldContainer>
					<FieldContainer>
						<FieldLabel>Project Name</FieldLabel>
						<FieldValue>{invoice.project_name}</FieldValue>
					</FieldContainer>
					<FieldContainer>
						<FieldLabel>Vendor Name</FieldLabel>
						<FieldValue>{invoice.vendor_name}</FieldValue>
					</FieldContainer>
				</CardRow>
				<CardRow>
					<FieldContainer>
						<FieldLabel>Initial Invoice Value</FieldLabel>
						<FieldValue>{formatCurrency(invoice.initial_invoice_value)}</FieldValue>
					</FieldContainer>
					<FieldContainer>
						<FieldLabel>Updated Invoice Value</FieldLabel>
						<FieldValue>{formatCurrency(invoice.updated_invoice_value)}</FieldValue>
					</FieldContainer>
					<FieldContainer>
						<FieldLabel>Billing Period</FieldLabel>
						<FieldValue>{invoice.billing_period}</FieldValue>
					</FieldContainer>
					<FieldContainer>
						<FieldLabel>Created at</FieldLabel>
						<FieldValue>{formatDate(invoice.created_at)}</FieldValue>
					</FieldContainer>
				</CardRow>
				<ActionButtons>
					<ViewButton
						onClick={() =>
							handleView(
								approval.entity_id || approval.id,
								approval.audit_id || approval.id
							)
						}
						title="View Details"
					>
						<SVG.EyeIconSVG />
					</ViewButton>
					{activeTab === 'pending' && (
						<>
							<ACL action="update" module="vendor_bill_changes_accept">
								<Button
									type="primary"
									onClick={() => handleApproveClick(approval)}
									disabled={actionLoading}
								>
									Accept
								</Button>
							</ACL>
							<ACL action="update" module="vendor_bill_changes_reject">
								<Button
									danger
									onClick={() => handleRejectClick(approval)}
									disabled={actionLoading}
								>
									Reject
								</Button>
							</ACL>
						</>
					)}
				</ActionButtons>
			</Card>
		);
	};

	const renderContent = () => {
		if (loading) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading approvals..." />
				</Box>
			);
		}

		if (!approvals || approvals.length === 0) {
			return (
				<Box padding="60px 20px" textAlign="center">
					<Empty
						description={
							<Text fontSize="14px" color="#999">
								No {activeTab} invoice edit approvals found
							</Text>
						}
					/>
				</Box>
			);
		}

		// Use table for approved/rejected tabs, cards for pending tab
		if (activeTab === 'approved' || activeTab === 'rejected') {
			return renderTableContent(activeTab);
		}

		// Use cards for pending tab
		return <Box>{approvals.map(renderApprovalCard)}</Box>;
	};

	const tabItems = [
		{
			key: 'pending',
			label: 'Pending',
			children: renderContent(),
		},
		{
			key: 'approved',
			label: 'Approved',
			children: renderContent(),
		},
		{
			key: 'rejected',
			label: 'Rejected',
			children: renderContent(),
		},
	];

	return (
		<Container>
			<HeaderContainer>
				<Box>
					<Title>Invoice Edit Approvals</Title>
					<Subtitle>Approve or reject vendor bill change requests</Subtitle>
				</Box>
			</HeaderContainer>

			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="line" />

			{/* Approve Modal */}
			<Modal
				title="Accept Invoice Changes"
				open={approveModalVisible}
				onOk={handleApproveConfirm}
				onCancel={() => {
					setApproveModalVisible(false);
					setSelectedApproval(null);
				}}
				okText="Accept Changes"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading,
				}}
			>
				<Box>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This will accept all pending changes to the vendor bill and recalculate the
						total payable amount. You won&apos;t be able to revert this change.
					</Text>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Invoice Changes"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setSelectedApproval(null);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading,
					danger: true,
				}}
			>
				<Box>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject these invoice edit changes? The vendor bill
						will remain unchanged.
					</Text>
					{selectedApproval && (
						<Box mt={2} p={2} background="#f5f5f5" borderRadius="4px">
							<Text fontSize="12px" color="#666" display="block">
								Invoice No:{' '}
								{selectedApproval.entity_summary?.vendor_bill_no ||
									selectedApproval.vendor_bill_no ||
									'-'}
							</Text>
							<Text fontSize="12px" color="#666" display="block">
								Edit ID: {selectedApproval.audit_id || selectedApproval.id || '-'}
							</Text>
						</Box>
					)}
					<Box mt={3}>
						<Text fontSize="14px" color="#333" mb={1} display="block">
							Rejection Reason (Optional)
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter reason for rejection (optional)..."
							maxLength={1000}
							showCount
						/>
					</Box>
				</Box>
			</Modal>
		</Container>
	);
}
